import React from "react";
import { Checkbox } from "@mui/material";
import { Table, Column, AutoSizer } from "react-virtualized";
import "react-virtualized/styles.css"; // Default styles (can be overridden)
import { useMaterialUIController } from "context";

// Assuming you have the darkMode state from context
export const VirtualizedTableData = ({
  initialRowIndex,
  isSelection,
  isMultiSelect,
  selectedAll,
  selectedRows,
  setSelectedRows,
  onRowClick,
  onRowRedirect,
  setSelectedAll,
  memoizedRows,
  memoizedColumns,
  setInitialRowIndex,
  setCurrentRowIndex,
}) => {
  selectedRows ??= [];

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const toggleRowSelection = (rowId) => {
    if (typeof setSelectedRows !== "function") return;
    setSelectedRows((prevSelectedRows) => {
      const rowSet = new Set(prevSelectedRows);
      rowSet.has(rowId) ? rowSet.delete(rowId) : rowSet.add(rowId);
      return Array.from(rowSet);
    });
  };

  const toggleSelectAll = (selectedAll) => {
    setSelectedRows(selectedAll ? memoizedRows.map((row) => row.id) : []);
    setSelectedAll(selectedAll);
  };

  const handleSelectRowBetween = (index) => {
    let diff = initialRowIndex - index;
    let selected;
    if (diff < 0) {
      selected = memoizedRows.slice(initialRowIndex, index + 1);
    } else {
      selected = memoizedRows.slice(index, initialRowIndex + 1);
    }
    setSelectedRows((previousSelected) => [...new Set([...previousSelected, ...selected?.map((select) => select.id)])]);
  };

  return (
    <div style={{ width: "100%", height: "100%", overflowY: "hidden", overflowX: "auto" }}>
      <AutoSizer>
        {({ width, height }) => (<>
          <Table
            width={width}
            height={height}
            headerHeight={60}
            rowHeight={50}
            rowCount={memoizedRows.length}
            rowGetter={({ index }) => memoizedRows[index]}
            rowStyle={({ index }) => {
              const row = memoizedRows[index];
              const isSelected = row && selectedRows.includes(row.id);

              return {
                cursor: "pointer",
                borderBottom: "0.0625rem solid #f0f2f566",
                "&:hover": {
                  backgroundColor:
                    selectedRows && isMultiSelect && isSelected
                      ? "rgba(0, 0, 255, 0.2)" // Maintain the selected color when hovered
                      : "rgba(0, 0, 0, 0.04)", // Hover color
                },
                // Background color when the row is selected
                backgroundColor:
                  selectedRows && isMultiSelect && isSelected
                    ? "rgba(0, 0, 255, 0.2)" // Selected color
                    : "", // Default background color
              };
            }}
            onRowClick={({ event, rowData, index }) => {
              if (!event.target.closest("input[type='checkbox']")) {
                if (onRowRedirect) {
                  onRowRedirect(rowData.id);
                } else {
                  toggleRowSelection(rowData.id);
                  if (selectedRows?.length <= 0) {
                    onRowClick(rowData.id);
                    setInitialRowIndex(index);
                    setCurrentRowIndex(index);
                  } else if (event.shiftKey) {
                    handleSelectRowBetween(index);
                  }
                }
              }
            }}
          >
            {/* Selection Column */}
            {isSelection && isMultiSelect && (
              <Column
                label={
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < memoizedRows.length}
                    checked={selectedAll}
                    onChange={() => toggleSelectAll(!selectedAll)}
                  />
                }
                dataKey="checkbox"
                cellRenderer={({ rowData, rowIndex }) => (
                  <Checkbox
                    checked={Array.isArray(selectedRows) && isMultiSelect ? selectedRows.includes(rowData.id) : selectedRows == rowData.id}
                    onClick={(e) => {
                      toggleRowSelection(rowData.id);
                      if (selectedRows.length <= 0) {
                        setInitialRowIndex(rowIndex);
                        setCurrentRowIndex(rowIndex);
                      } else if (e.shiftKey) {
                        handleSelectRowBetween(rowIndex);
                      }
                    }}
                  />
                )}
                flexShrink={0}
                width={70}
                headerStyle={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "0 16px",
                  backgroundColor: darkMode ? "#52566D" : "#52566D",
                  margin: 0,
                  opacity: 0.7,
                  userSelect: "none",
                }}
                style={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  margin: 0,
                }}
              />
            )}

            {/* All Data columns */}
            {memoizedColumns.map((col) => (
              <Column
                key={col.accessor}
                label={col.Header}
                dataKey={col.accessor}
                cellRenderer={({ rowData }) => (col.Cell ? col.Cell({ row: rowData }) : rowData[col.accessor])}
                width={col.width}
                flexGrow={col.grow ? 1 : 0}
                flexShrink={col.notShrink ? 0 : 1}
                headerStyle={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  height: "100%",
                  padding: "0 24px",
                  backgroundColor: darkMode ? "#52566D" : "#52566D",
                  margin: 0,
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "white",
                  // opacity: 0.7,
                  userSelect: "none",
                }}
                style={{
                  textAlign: col.align || "left",
                  padding: "0 24px",
                  margin: 0,
                  fontSize: "13.5px",
                  fontWeight: "lighter",
                  color: "white",
                  overflow: "hidden",
                  whiteSpace: "normal",
                }}
              />
            ))}
          </Table>
        </>
        )}
      </AutoSizer>
    </div>
  );
};
